import React, { useContext, useState } from 'react';
import { useClickRef } from '@make-software/csprclick-ui';
import styled from 'styled-components';
import Big from 'big.js';
import { Args, CLTypeUInt8, CLValue, Hash, PublicKey, SessionBuilder } from 'casper-js-sdk';
import { TransactionStatus } from '@make-software/csprclick-core-types';
import { SvgIcon } from '@make-software/cspr-design';

import LoadingIcon from '@/assets/icons/loading.svg';
import proxyCallerUrl from '@/assets/proxy_caller.wasm?url';
import { ConnectedAccountContext } from '@/contexts';
import { TextModal } from '@/components';
import { BuyStampsForm } from './buy-stamps-form';

const StyledLoadingIcon = styled(SvgIcon)(() => ({
  animationName: 'spin',
  animationDuration: '5000ms',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear'
}));

const buildBuyStampsTransaction = async (
  caller: string,
  amount: string,
) => {
  const proxyCallerResponse = await fetch(proxyCallerUrl);
  const contractWasm = new Uint8Array(await proxyCallerResponse.arrayBuffer());

  const amountMotes = new Big(amount)
    .mul(config.stamp_cost)
    .mul(1000000000)
    .toString();

  const args = Args.fromMap({
    amount: CLValue.newCLUInt512(amountMotes),
    attached_value: CLValue.newCLUInt512(amountMotes),
    entry_point: CLValue.newCLString('buy_stamps'),
    package_hash: CLValue.newCLByteArray(
      Hash.fromHex(config.chainmail_contract_package_hash).toBytes()
    ),
    args: CLValue.newCLList(
      CLTypeUInt8,
      Array.from(Args.fromMap({}).toBytes())
        .map((value) => CLValue.newCLUint8(value))
    )
  });

  return new SessionBuilder()
    .from(PublicKey.fromHex(caller))
    .runtimeArgs(args)
    .wasm(contractWasm)
    .payment(config.stamp_transaction_cost * 1000000000)
    .chainName(window.csprclick?.chainName!)
    .build()
    .toJSON();
};

type BuyStampsFlowStep = 'form'
  | 'loading'
  | 'success'
  | 'cancelled'
  | 'error';

export interface BuyStampsFlowProps {
  onSuccess: () => void;
}

export const BuyStampsFlow: React.FC<BuyStampsFlowProps> = ({ onSuccess }) => {
  const [flowStep, setFlowStep] = useState<BuyStampsFlowStep>('form');
  const { connectedAccount, refreshStampBalance } = useContext(ConnectedAccountContext);
  const clickRef = useClickRef();

  const submitBuyStampsTransaction = async (amount: string) => {
    const caller = connectedAccount?.public_key?.toLowerCase() || '';

    const transaction = await buildBuyStampsTransaction(caller, amount);

    const onStatusUpdate = (status: string, data: any) => {
      if (status === TransactionStatus.CANCELLED) {
        setFlowStep('cancelled');
      }
      else if (status === TransactionStatus.ERROR) {
        console.error('Error: ', data?.error + '(' + data?.errorData + ')');
        setFlowStep('error');
      }
      else if (status === TransactionStatus.PROCESSED) {
        if (data.csprCloudTransaction?.error_message === null) {
            setTimeout(refreshStampBalance, 2000);
            setFlowStep('success');
            onSuccess();
        } else {
          console.error('Error: ', data?.error + '(' + data?.errorData + ')');
          setFlowStep('error');
        }
      }
    };

    setFlowStep('loading');

    clickRef?.send(transaction as object, caller, onStatusUpdate).catch((err: any) => {
      setFlowStep('error');
      alert('Error: ' + err);
    });
  };

  return (
    <>
      {flowStep === 'loading' && (
        <TextModal
          icon={<StyledLoadingIcon src={LoadingIcon} width={100} height={100}/>}
          text="Purchasing stamps"
        />
      )}
      {flowStep === 'success' && (
        <TextModal text="You have successfully purchased stamps!"/>
      )}
      {flowStep === 'cancelled' && (
        <TextModal text="Your request have been cancelled by the wallet application"/>
      )}
      {flowStep === 'error' && (
        <TextModal text="Something went wrong. Please try again later."/>
      )}
      {flowStep === 'form' && (
        <BuyStampsForm onSubmit={submitBuyStampsTransaction} />
      )}
    </>
  );
};
